import React from "react";
import styled from "styled-components";
import {
  toREM,
  toVWDesktop,
  toVWMobile,
} from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import ButtonLink, {
  ButtonType,
} from "../shared/ButtonLink";
import Arrow from "../../assets/images/diagonal_arrow.svg";

export type WebstaurantSectionProps = {
  title: string;
  subtitle: string;
  ctaLabel: string;
  ctaUrl: string;
  externalLink?: boolean;
  logo?: {
    src?: string;
    alt?: string;
  };
};

const WebstaurantSection = ({
  title,
  subtitle,
  ctaLabel,
  ctaUrl,
  externalLink,
  logo,
}: WebstaurantSectionProps) => {
  if (
    !title ||
    !subtitle ||
    !ctaLabel ||
    !ctaUrl ||
    !logo?.src
  )
    return null;
  return (
    <Container>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
      <WebrestaurantContainer>
        <WebrestaurantLogoImg
          src={logo?.src}
          alt={logo?.alt}
        />
        <Btn
          type={ButtonType.PRIMARY_LIGHT}
          label={ctaLabel}
          link={ctaUrl}
          blank={externalLink}
          iconComponent={
            externalLink ? (
              <Icon src={Arrow} alt="" />
            ) : undefined
          }
        />
      </WebrestaurantContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${toVWMobile(20)}vw ${toVWMobile(48)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    padding: 0 ${toVWDesktop(20)}vw ${toVWDesktop(80)}vw;
  }
`;

const Title = styled.h4`
  color: var(--color-white);
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(24)}rem;
  font-weight: 900;
  font-style: normal;
  letter-spacing: ${toREM(0.72)}rem;
  line-height: 1.08;
  margin-bottom: ${toVWMobile(15)}vw;
  text-align: center;
  text-transform: uppercase;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    letter-spacing: ${toREM(36 * 0.03)}rem;
    font-size: ${toREM(36)}rem;
    margin-bottom: ${toVWDesktop(15)}vw;
  }
`;

const Subtitle = styled.p`
  color: var(--color-white);
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  font-size: ${toREM(18)}rem;
  font-weight: 500;
  font-style: normal;
  line-height: 1.4;
  text-align: center;
  max-width: ${toVWMobile(300)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    max-width: none;
  }
`;

const WebrestaurantContainer = styled.div`
  background-color: var(--color-white);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  flex-direction: column;
  padding: ${toVWMobile(12)}vw;
  padding-top: ${toVWMobile(20)}vw;
  gap: ${toVWMobile(24)}vw;
  margin-top: ${toVWMobile(40)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: auto;
    padding: ${toVWDesktop(8)}vw;
    padding-left: ${toVWDesktop(24)}vw;
    gap: ${toVWDesktop(24)}vw;
    margin-top: ${toVWDesktop(40)}vw;
    flex-direction: row;
  }
`;

const WebrestaurantLogoImg = styled.img`
  height: ${toVWMobile(16)}vw;
  width: auto;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    height: ${toVWDesktop(16)}vw;
  }
`;

const Btn = styled(ButtonLink)`
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  align-items: center;
`;

const Icon = styled.img`
  width: ${toVWMobile(12)}vw;
  height: ${toVWMobile(12)}vw;
  margin-left: ${toVWMobile(10)}vw;

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${toVWDesktop(14)}vw;
    height: ${toVWDesktop(14)}vw;
    margin-left: ${toVWDesktop(10)}vw;
  }
`;

export default WebstaurantSection;
